<!--
  Admin mode › Flexi › Settings › Exposure assessment templates: the
  application's templates, which of them new businesses get, and who has each.
-->
<flexi-settings-nav></flexi-settings-nav>

<div class="fvr-lib" [attr.aria-busy]="loading">
  <section class="fvr-lib__main">
    <header class="fvr-head">
      <div class="fvr-lib__heading">
        <h2 class="fvr-title">{{ 'FLEXI_EXPOSURE.LIBRARY.TITLE' | translate }}</h2>
        <p class="fvr-subtitle">{{ 'FLEXI_EXPOSURE.LIBRARY.INTRO' | translate }}</p>
      </div>
      <button type="button" class="fas-btn fas-btn--primary" (click)="openCreate()">
        <i class="pi pi-plus" aria-hidden="true"></i>{{ 'FLEXI_EXPOSURE.LIBRARY.NEW' | translate }}
      </button>
    </header>

    <div class="fvr-lib__toolbar">
      <label class="fvr-search">
        <span class="fas-sr-only">{{ 'FLEXI_EXPOSURE.LIBRARY.SEARCH' | translate }}</span>
        <i class="pi pi-search" aria-hidden="true"></i>
        <input
          pInputText
          type="search"
          maxlength="200"
          [(ngModel)]="search"
          (ngModelChange)="applyFilter()"
          [placeholder]="'FLEXI_EXPOSURE.LIBRARY.SEARCH' | translate"
        />
      </label>
      <div class="fvr-filter" role="group" [attr.aria-label]="'FLEXI_EXPOSURE.LIBRARY.STATUS_FILTER' | translate">
        @for (f of filters; track f.labelKey) {
          <button
            type="button"
            class="fvr-filter__chip"
            [class.fvr-filter__chip--active]="status === f.value"
            [attr.aria-pressed]="status === f.value"
            (click)="pickStatus(f.value)"
          >
            {{ f.labelKey | translate }}
            <span class="fvr-filter__count">{{ count(f.value) }}</span>
          </button>
        }
      </div>
    </div>

    @if (loadFailed) {
      <p class="fas-error" role="alert">{{ 'FLEXI_EXPOSURE.LOAD_FAILED' | translate }}</p>
      <button type="button" class="fas-btn fas-btn--ghost" (click)="load()">{{ 'FLEXI_EXPOSURE.RETRY' | translate }}</button>
    }

    @if (loading) { <flexi-safety-round-skeleton /> }
    @if (busyIds.size || creating) { <p class="fvr-action-status" role="status">{{ 'FLEXI_EXPOSURE.SAVING' | translate }}</p> }
    <div class="fvr-table-card" *ngIf="!loading && !loadFailed">
      <p-table
        [value]="visible"
        dataKey="Id"
        [rows]="20"
        [paginator]="visible.length > 20"
        [rowHover]="true"
        styleClass="p-datatable-sm"
        [tableStyle]="{ 'min-width': '56rem' }"
      >
        <ng-template pTemplate="header">
          <tr>
            <th>{{ 'FLEXI_EXPOSURE.LIBRARY.COLUMN_TEMPLATE' | translate }}</th>
            <th>{{ 'FLEXI_EXPOSURE.LIBRARY.COLUMN_CONTENT' | translate }}</th>
            <th>{{ 'FLEXI_EXPOSURE.LIBRARY.COLUMN_ASSIGNED' | translate }}</th>
            <th>{{ 'FLEXI_EXPOSURE.LIBRARY.COLUMN_DEFAULT' | translate }}</th>
            <th>{{ 'FLEXI_EXPOSURE.COLUMN.STATUS' | translate }}</th>
            <th class="fvr-lib__action-col"><span class="fas-sr-only">{{ 'ACTIONS' | translate }}</span></th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-t>
          <tr>
            <td>
              <div class="fvr-lib__name">
                <a class="fvr-link" href="javascript:void(0)" (click)="open(t)">{{ t.Name }}</a>
                <span class="fas-muted">
                  {{ 'FLEXI_EXPOSURE.VERSION' | translate: { version: t.Version } }}
                  · {{ 'FLEXI_EXPOSURE.LIBRARY.CHANGED' | translate: { date: ((t.ModifiedOn || t.CreatedOn) | date: 'dd-MM-yyyy') } }}
                </span>
              </div>
            </td>
            <td class="fvr-num">
              {{ 'FLEXI_EXPOSURE.LIBRARY.CONTENT' | translate: { factors: t.FactorCount, sections: t.SectionCount } }}
            </td>
            <td class="fvr-num">
              <button
                type="button"
                class="fas-btn fas-btn--text"
                [attr.aria-label]="'FLEXI_EXPOSURE.ASSIGN.MANAGE_TITLE' | translate: { name: t.Name }"
                (click)="openAssign(t)"
              >
                {{ 'FLEXI_EXPOSURE.LIBRARY.ASSIGNED_COUNT' | translate: { count: t.AssignedCount } }}
              </button>
            </td>
            <td>
              <button
                type="button"
                role="switch"
                class="fvr-switch"
                [attr.aria-checked]="t.IsDefaultForNewBusinesses"
                [attr.aria-label]="'FLEXI_EXPOSURE.LIBRARY.DEFAULT_ARIA' | translate: { name: t.Name }"
                [disabled]="!isPublished(t) || busyIds.has(t.Id)"
                [pTooltip]="!isPublished(t) ? ('FLEXI_EXPOSURE.LIBRARY.DEFAULT_NEEDS_PUBLISHED' | translate) : ''"
                (click)="toggleDefault(t)"
              ></button>
            </td>
            <td>
              <span class="fvr-pill" [ngClass]="statusClass(t.Status)">{{ statusKey(t.Status) | translate }}</span>
            </td>
            <td class="fvr-lib__action-col">
              @if (isPublished(t)) {
                <button type="button" class="fas-btn fas-btn--text" (click)="openAssign(t)">
                  {{ 'FLEXI_EXPOSURE.LIBRARY.ASSIGN' | translate }}
                </button>
              }
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="6">
              <div class="fvr-empty">
                <i class="pi pi-inbox" aria-hidden="true"></i>
                <p>{{ 'FLEXI_EXPOSURE.LIBRARY.EMPTY' | translate }}</p>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  </section>

  <aside class="fvr-lib__side" *ngIf="!loading && !loadFailed">
    <section class="fvr-card fvr-lib__aside-card">
      <span class="fvr-eyebrow">{{ 'FLEXI_EXPOSURE.LIBRARY.NEW_BUSINESSES_GET' | translate }}</span>
      <strong>{{ defaultsForNew.length ? defaultsForNewNames : ('FLEXI_EXPOSURE.LIBRARY.NO_DEFAULTS' | translate) }}</strong>
      <p class="fas-muted">{{ 'FLEXI_EXPOSURE.LIBRARY.NEW_BUSINESSES_NOTE' | translate }}</p>
    </section>
    @if (drafts.length) {
      <section class="fvr-card fvr-lib__aside-card">
        <span class="fvr-eyebrow">{{ 'FLEXI_EXPOSURE.TEMPLATE_STATUS.0' | translate }}</span>
        <p class="fas-muted">{{ 'FLEXI_EXPOSURE.LIBRARY.DRAFTS_NOTE' | translate: { names: draftNames } }}</p>
      </section>
    }
    <p class="fas-note">
      <i class="pi pi-info-circle" aria-hidden="true"></i>
      <span>{{ 'FLEXI_EXPOSURE.LIBRARY.SNAPSHOT_NOTE' | translate }}</span>
    </p>
  </aside>
</div>

@if (assigning; as t) {
  <flexi-exposure-assign-drawer
    [templateId]="t.Id"
    [templateName]="t.Name"
    [canAssign]="isPublished(t)"
    (closed)="assignClosed($event)"
  ></flexi-exposure-assign-drawer>
}

<p-dialog
  [visible]="createOpen"
  (visibleChange)="$event ? null : closeCreate()"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [closable]="!creating"
  [style]="{ width: '560px', maxWidth: '95vw' }"
  [header]="'FLEXI_EXPOSURE.LIBRARY.NEW_TITLE' | translate"
>
  <form class="fvr-create" id="fer-create-form" (ngSubmit)="create()">
    <div class="fvr-field">
      <label for="fer-new-name">{{ 'FLEXI_EXPOSURE.EDITOR.NAME' | translate }} <span class="fvr-required">*</span></label>
      <input pInputText id="fer-new-name" name="name" type="text" maxlength="200" [(ngModel)]="newName" />
    </div>
    <div class="fvr-field">
      <label for="fer-new-description">{{ 'FLEXI_EXPOSURE.EDITOR.DESCRIPTION' | translate }}</label>
      <textarea pTextarea id="fer-new-description" name="description" rows="3" maxlength="1000" [(ngModel)]="newDescription"></textarea>
    </div>
  </form>

  <ng-template pTemplate="footer">
    <div class="fas-dialog-actions fvr-create__actions">
      <button type="button" class="fas-btn fas-btn--ghost" [disabled]="creating" (click)="closeCreate()">{{ 'CANCEL' | translate }}</button>
      <button type="submit" form="fer-create-form" class="fas-btn fas-btn--primary" [disabled]="creating || !newName.trim()">
        @if (creating) {
          <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
        }
        {{ (creating ? 'FLEXI_EXPOSURE.SAVING' : 'FLEXI_EXPOSURE.LIBRARY.CREATE') | translate }}
      </button>
    </div>
  </ng-template>
</p-dialog>
